'use client';

import { Listbox, Transition } from '@headlessui/react';
import { CheckIcon, ChevronDownIcon } from '@heroicons/react/20/solid';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $patchStyleText } from '@lexical/selection';
import {
    $getRoot,
    $getSelection,
    $isRangeSelection,
    $isTextNode,
} from 'lexical';
import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { MediaPanel } from '../MediaPanel';
import { appendGradientColor, ColorPanel } from './TextColorButton';

const FALLBACK_PRIMARY = '#1b7ce4';
const FALLBACK_SECONDARY = '#1ab261';

const MARKER_OPTIONS = [
    { value: 'line', label: 'Line', sizeLabel: 'Scale' },
    { value: 'wavy', label: 'Wavy', sizeLabel: 'Wave size' },
    { value: 'circle', label: 'Circle', sizeLabel: 'Outline size' },
    { value: 'highlight', label: 'Highlight', sizeLabel: 'Fill size' },
    { value: 'underline', label: 'Underline', sizeLabel: 'Stroke size' },
    {
        value: 'double-underline',
        label: 'Double underline',
        sizeLabel: 'Stroke size',
    },
    {
        value: 'strikethrough',
        label: 'Strikethrough',
        sizeLabel: 'Stroke size',
    },
    { value: 'cross-out', label: 'Cross-out', sizeLabel: 'Stroke size' },
    { value: 'arrow', label: 'Arrow', sizeLabel: 'Arrow size' },
    { value: 'bracket', label: 'Bracket' },
    { value: 'box', label: 'Box', sizeLabel: 'Outline size' },
    { value: 'custom', label: 'Custom', sizeLabel: 'Custom size' },
];

const DEFAULT_MOTION = {
    durationMs: 700,
    delayMs: 0,
    size: 1,
    animate: true,
    thickness: 2,
    dotSize: 1.5,
    radius: 32,
    offsetY: 0,
};

const isSvgImage = (image) =>
    /\.svg(?:$|\?)/i.test(String(image?.url || image?.filename || ''));

function getCssColor(variable, fallback) {
    if (typeof window === 'undefined') return fallback;
    const value = getComputedStyle(document.documentElement)
        .getPropertyValue(variable)
        .trim();
    return /^#[0-9a-f]{6}$/i.test(value) ? value : fallback;
}

function parseStyles(style = '') {
    return style.split(';').reduce((result, declaration) => {
        const colon = declaration.indexOf(':');
        if (colon < 0) return result;
        const property = declaration.slice(0, colon).trim().toLowerCase();
        const value = declaration.slice(colon + 1).trim();
        if (property && value) result[property] = value;
        return result;
    }, {});
}

function rgbToHex(value) {
    const match = String(value || '').match(
        /^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i
    );
    if (!match) return value;
    return `#${match
        .slice(1, 4)
        .map((channel) => Number(channel).toString(16).padStart(2, '0'))
        .join('')}`;
}

function expandSelectionToAllIfNone() {
    let selection = $getSelection();
    if (!$isRangeSelection(selection) || selection.isCollapsed()) {
        const root = $getRoot();
        root.select(0, root.getTextContentSize());
        selection = $getSelection();
    }
    return $isRangeSelection(selection) ? selection : null;
}

function patchEverySelectedTextNode(selection, stylePatch) {
    // Let Lexical split partially selected boundary nodes first.
    $patchStyleText(selection, stylePatch);
    const updatedSelection = $getSelection();
    if (!$isRangeSelection(updatedSelection)) return;

    // MDX styled spans can preserve an older style on a fully selected node
    // between the range boundaries. Patch every resulting selected TextNode
    // explicitly so a larger effect truly replaces a smaller nested one.
    for (const node of updatedSelection.getNodes()) {
        if (!$isTextNode(node)) continue;
        const styles = parseStyles(node.getStyle());
        for (const [property, value] of Object.entries(stylePatch)) {
            if (value == null || value === '') delete styles[property];
            else styles[property] = String(value);
        }
        node.setStyle(
            Object.entries(styles)
                .map(([property, value]) => `${property}: ${value}`)
                .join('; ')
        );
    }
}

function getSelectionMarker(editor) {
    const result = {
        variant: 'line',
        colors: [],
        angle: 45,
        ...DEFAULT_MOTION,
        customSvg: '',
        customImage: '',
        layer: 'under',
        direction: 'left',
    };
    editor.getEditorState().read(() => {
        const selection = $getSelection();
        if (!$isRangeSelection(selection)) return;
        for (const node of selection.getNodes()) {
            if (typeof node.getStyle !== 'function') continue;
            const styles = parseStyles(node.getStyle());
            if (!styles['--editor-marker']) continue;
            result.variant = styles['--editor-marker'];
            const gradient = styles['--editor-marker-gradient'] || '';
            const angle = gradient.match(/(-?\d+(?:\.\d+)?)deg/i)?.[1];
            const markerAngle = Number(styles['--editor-marker-angle']);
            result.colors = (
                gradient.match(/rgba?\([^)]+\)|#[0-9a-f]{3,6}/gi) || []
            ).map(rgbToHex);
            if (
                result.colors.length === 2 &&
                result.colors[0].toLowerCase() ===
                    result.colors[1].toLowerCase()
            ) {
                result.colors = [result.colors[0]];
            }
            if (Number.isFinite(markerAngle)) result.angle = markerAngle;
            else if (angle !== undefined) result.angle = Number(angle);
            const durationMs = Number(styles['--editor-marker-duration']);
            const delayMs = Number(styles['--editor-marker-delay']);
            const size = Number(styles['--editor-marker-size']);
            const thickness = parseFloat(
                styles['--editor-marker-thickness'] || ''
            );
            const dotSize = parseFloat(
                styles['--editor-marker-dot-size'] || ''
            );
            const radius = parseFloat(styles['--editor-marker-radius'] || '');
            const offsetY = parseFloat(
                styles['--editor-marker-offset-y'] || ''
            );
            if (Number.isFinite(durationMs)) {
                result.durationMs = Math.min(5000, Math.max(0, durationMs));
            }
            if (Number.isFinite(delayMs)) {
                result.delayMs = Math.min(2000, Math.max(0, delayMs));
            }
            if (Number.isFinite(size)) result.size = size;
            if (Number.isFinite(thickness)) result.thickness = thickness;
            if (Number.isFinite(dotSize)) result.dotSize = dotSize;
            if (Number.isFinite(radius)) result.radius = radius;
            if (Number.isFinite(offsetY)) result.offsetY = offsetY;
            result.animate = styles['--editor-marker-animate'] !== '0';
            result.customSvg = styles['--editor-marker-svg'] || '';
            result.customImage = styles['--editor-marker-image'] || '';
            result.direction =
                styles['--editor-marker-direction'] ||
                (result.variant === 'highlight' ? 'left' : 'center');
            result.layer =
                styles['--editor-marker-layer'] ||
                (result.variant === 'strikethrough' ||
                result.variant === 'cross-out'
                    ? 'over'
                    : 'under');
            break;
        }
    });
    return result;
}

export function TextMarkerControl({
    open,
    onMarkdownSync,
    onRestoreSelection,
    sharedAngle,
    onSharedAngleChange,
}) {
    const [editor] = useLexicalComposerContext();
    const [variant, setVariant] = useState('line');
    const [colors, setColors] = useState([FALLBACK_PRIMARY]);
    const [angle, setAngle] = useState(45);
    const [durationMs, setDurationMs] = useState(DEFAULT_MOTION.durationMs);
    const [delayMs, setDelayMs] = useState(DEFAULT_MOTION.delayMs);
    const [size, setSize] = useState(DEFAULT_MOTION.size);
    const [thickness, setThickness] = useState(DEFAULT_MOTION.thickness);
    const [dotSize, setDotSize] = useState(DEFAULT_MOTION.dotSize);
    const [radius, setRadius] = useState(DEFAULT_MOTION.radius);
    const [offsetY, setOffsetY] = useState(DEFAULT_MOTION.offsetY);
    const [animate, setAnimate] = useState(DEFAULT_MOTION.animate);
    const [customSvg, setCustomSvg] = useState('');
    const [customImage, setCustomImage] = useState('');
    const [layer, setLayer] = useState('under');
    const [direction, setDirection] = useState('left');
    const primaryRef = useRef(FALLBACK_PRIMARY);
    const secondaryRef = useRef(FALLBACK_SECONDARY);

    const setMarkerAngle = useCallback(
        (nextAngle) => {
            setAngle(nextAngle);
            onSharedAngleChange?.(nextAngle);
        },
        [onSharedAngleChange]
    );
    const displayedAngle = Number.isFinite(Number(sharedAngle))
        ? Number(sharedAngle)
        : Number(angle);

    useEffect(() => {
        primaryRef.current = getCssColor('--primary-700', FALLBACK_PRIMARY);
        secondaryRef.current = getCssColor(
            '--secondary-700',
            FALLBACK_SECONDARY
        );
        setColors((current) =>
            current[0] === FALLBACK_PRIMARY
                ? [primaryRef.current, ...current.slice(1)]
                : current
        );
    }, []);

    const prefillFromSelection = useCallback(() => {
        editor.update(() => onRestoreSelection?.());
        const current = getSelectionMarker(editor);
        setVariant(
            current.variant === 'dotted-underline' ? 'line' : current.variant
        );
        setColors(
            current.colors.length ? current.colors : [primaryRef.current]
        );
        setMarkerAngle(current.angle);
        setDurationMs(current.durationMs);
        setDelayMs(current.delayMs);
        setSize(current.size);
        setThickness(current.thickness);
        setDotSize(current.dotSize);
        setRadius(current.radius);
        setOffsetY(current.offsetY);
        setAnimate(current.animate);
        setCustomSvg(current.customSvg || '');
        setCustomImage(current.customImage || '');
        setLayer(current.layer || 'under');
        setDirection(
            current.direction ||
                (current.variant === 'highlight' ? 'left' : 'center')
        );
    }, [editor, onRestoreSelection, setMarkerAngle]);

    useEffect(() => {
        if (open) prefillFromSelection();
    }, [open, prefillFromSelection]);

    const updateColor = (index, color) => {
        setColors((current) =>
            current.map((item, itemIndex) =>
                itemIndex === index ? color : item
            )
        );
    };

    const addColor = () => {
        setColors((current) =>
            appendGradientColor(
                current,
                primaryRef.current,
                secondaryRef.current
            )
        );
    };

    const removeColor = (index) => {
        setColors((current) =>
            current.length > 1
                ? current.filter((_, itemIndex) => itemIndex !== index)
                : current
        );
    };

    const handleHexInputChange = (event, index) => {
        const hex = event.target.value.replace(/[^0-9a-fA-F]/g, '').slice(0, 6);
        updateColor(index, `#${hex}`);
    };

    const applyMarker = () => {
        const validColors = colors.filter((color) =>
            /^#[0-9a-f]{6}$/i.test(color)
        );
        if (!validColors.length) return;
        const markerColors =
            validColors.length === 1
                ? [validColors[0], validColors[0]]
                : validColors;
        const clampedAngle = Math.max(
            0,
            Math.min(360, Number(displayedAngle) || 0)
        );
        const clampedDuration = Math.max(
            0,
            Math.min(5000, Number(durationMs) || 0)
        );
        const clampedDelay = Math.max(0, Math.min(2000, Number(delayMs) || 0));
        const clampedSize =
            variant === 'bracket'
                ? DEFAULT_MOTION.size
                : Math.max(0.5, Math.min(2.5, Number(size) || 1));
        const clampedThickness = Math.max(
            1,
            Math.min(64, Number(thickness) || DEFAULT_MOTION.thickness)
        );
        const clampedDotSize = Math.max(
            0.5,
            Math.min(8, Number(dotSize) || DEFAULT_MOTION.dotSize)
        );
        const clampedRadius = Math.max(0, Math.min(32, Number(radius) || 0));
        const clampedOffsetY = Math.max(
            -40,
            Math.min(40, Number(offsetY) || 0)
        );
        const shouldAnimate = variant === 'custom' ? false : animate;
        const cssGradientAngle = (clampedAngle + 90) % 360;
        const gradient = `linear-gradient(${cssGradientAngle}deg, ${markerColors.join(
            ', '
        )})`;

        editor.update(() => {
            const restored = onRestoreSelection?.();
            if (restored === false) return;
            const selection = expandSelectionToAllIfNone();
            if (!selection) return;
            patchEverySelectedTextNode(selection, {
                '--editor-marker': variant,
                '--editor-marker-color': validColors[0],
                '--editor-marker-gradient': gradient,
                '--editor-marker-angle': String(clampedAngle),
                '--editor-marker-duration': String(clampedDuration),
                '--editor-marker-delay': String(clampedDelay),
                '--editor-marker-size': String(clampedSize),
                '--editor-marker-thickness': `${clampedThickness}px`,
                '--editor-marker-dot-size': `${clampedDotSize}px`,
                '--editor-marker-radius': `${clampedRadius}px`,
                '--editor-marker-offset-y': `${clampedOffsetY}px`,
                '--editor-marker-replay': String(Date.now()),
                '--editor-marker-animate': shouldAnimate ? '1' : '0',
                '--editor-marker-svg': variant === 'custom' ? customSvg : null,
                '--editor-marker-image':
                    variant === 'custom' ? customImage : null,
                '--editor-marker-layer': layer,
                '--editor-marker-direction': direction,
                'box-decoration-break': 'clone',
                '-webkit-box-decoration-break': 'clone',
            });
        });
        onMarkdownSync?.();
    };

    const removeMarker = () => {
        editor.update(() => {
            const restored = onRestoreSelection?.();
            if (restored === false) return;
            const selection = expandSelectionToAllIfNone();
            if (!selection) return;
            patchEverySelectedTextNode(selection, {
                '--editor-marker': null,
                '--editor-marker-color': null,
                '--editor-marker-gradient': null,
                '--editor-marker-angle': null,
                '--editor-marker-duration': null,
                '--editor-marker-delay': null,
                '--editor-marker-size': null,
                '--editor-marker-thickness': null,
                '--editor-marker-dot-size': null,
                '--editor-marker-radius': null,
                '--editor-marker-offset-y': null,
                '--editor-marker-replay': null,
                '--editor-marker-animate': null,
                '--editor-marker-svg': null,
                '--editor-marker-image': null,
                '--editor-marker-layer': null,
                '--editor-marker-direction': null,
            });
        });
        onMarkdownSync?.();
    };

    const activeOption =
        MARKER_OPTIONS.find((option) => option.value === variant) ||
        MARKER_OPTIONS[0];

    const variantControl = (
        <Listbox
            value={variant}
            onChange={(nextVariant) => {
                setVariant(nextVariant);
                if (nextVariant === 'bracket') setSize(DEFAULT_MOTION.size);
                setMarkerAngle(
                    nextVariant === 'bracket'
                        ? 90
                        : nextVariant === 'line'
                        ? 45
                        : 0
                );
                setLayer(
                    nextVariant === 'strikethrough' ||
                        nextVariant === 'cross-out'
                        ? 'over'
                        : 'under'
                );
                setDirection(nextVariant === 'highlight' ? 'left' : 'center');
            }}
        >
            <div className="relative">
                <Listbox.Label className="mb-1 block text-[11px] font-medium text-neutral-700">
                    Effect
                </Listbox.Label>
                <div className="rounded-xl bg-neutral-300 p-px focus-within:bg-gradient-to-br focus-within:from-primary-600 focus-within:to-secondary-600">
                    <Listbox.Button
                        aria-label="Text effect"
                        className="relative flex h-8 w-full cursor-pointer items-center rounded-[11px] bg-white px-2.5 pr-8 text-left text-xs text-neutral-800 shadow-sm outline-none transition hover:bg-neutral-50 focus:outline-none"
                    >
                        <span className="truncate">{activeOption.label}</span>
                        <span className="pointer-events-none absolute inset-y-0 right-2 flex items-center">
                            <ChevronDownIcon className="h-3.5 w-3.5 text-neutral-500" />
                        </span>
                    </Listbox.Button>
                </div>
                <Transition
                    as={Fragment}
                    leave="transition ease-in duration-100"
                    leaveFrom="opacity-100 scale-100"
                    leaveTo="opacity-0 scale-95"
                >
                    <Listbox.Options className="absolute left-0 top-full z-[80] mt-1 max-h-60 w-full overflow-y-auto rounded-lg border border-neutral-200 bg-white p-1 text-xs shadow-xl ring-1 ring-black/5 focus:outline-none">
                        {MARKER_OPTIONS.map((option) => (
                            <Listbox.Option
                                key={option.value}
                                value={option.value}
                                className={({ active, selected }) =>
                                    [
                                        'flex cursor-pointer items-center justify-between rounded-md px-2 py-1.5',
                                        active
                                            ? 'bg-primary-50 text-primary-700'
                                            : 'text-neutral-800',
                                        selected ? 'font-medium' : '',
                                    ].join(' ')
                                }
                            >
                                {({ selected }) => (
                                    <>
                                        <span className="truncate">
                                            {option.label}
                                        </span>
                                        {selected && (
                                            <CheckIcon className="h-3.5 w-3.5 shrink-0 text-primary-600" />
                                        )}
                                    </>
                                )}
                            </Listbox.Option>
                        ))}
                    </Listbox.Options>
                </Transition>
            </div>
        </Listbox>
    );

    const effectControls = (
        <fieldset className="space-y-2 border-t border-neutral-200 pt-2">
            {variant === 'custom' && (
                <div className="space-y-2 rounded-lg border border-neutral-200 bg-white p-2">
                    <MediaPanel
                        inline
                        hidePreview
                        enableVideo={false}
                        currentValue={customSvg || customImage}
                        imageFilter={isSvgImage}
                        showStockImages={false}
                        inputPlaceholder="Select an SVG or paste SVG code…"
                        inputHelperText="SVG image URL or SVG code"
                        onSelectImage={(value) => {
                            const next = String(value || '').trim();
                            if (/^<\s*svg[\s>]/i.test(next)) {
                                setCustomSvg(next);
                                setCustomImage('');
                            } else {
                                setCustomImage(next);
                                setCustomSvg('');
                            }
                        }}
                        onSelectCustom={(value) => {
                            setCustomSvg(String(value || '').trim());
                            setCustomImage('');
                        }}
                    />
                </div>
            )}
            <div className="grid grid-cols-3 gap-2">
                {variant !== 'bracket' && (
                    <label className="block">
                        <span className="mb-1 block text-[10px] font-medium text-neutral-600">
                            {activeOption.sizeLabel} ({Number(size).toFixed(1)}
                            ×)
                        </span>
                        <input
                            aria-label={`${activeOption.sizeLabel} slider`}
                            type="range"
                            min="0.5"
                            max="2.5"
                            step="0.1"
                            value={size}
                            onChange={(event) => setSize(event.target.value)}
                            className="tw-slider mb-1 w-full cursor-pointer"
                        />
                    </label>
                )}
                {variant === 'line' && (
                    <>
                        <label className="block">
                            <span className="mb-1 block text-[10px] font-medium text-neutral-600">
                                Thickness ({Number(thickness).toFixed(1)}px)
                            </span>
                            <input
                                aria-label="Line thickness slider"
                                type="range"
                                min="1"
                                max="64"
                                step="0.5"
                                value={thickness}
                                onChange={(event) =>
                                    setThickness(event.target.value)
                                }
                                className="tw-slider mb-1 w-full cursor-pointer"
                            />
                        </label>
                        <label className="block">
                            <span className="mb-1 block text-[10px] font-medium text-neutral-600">
                                Corner radius ({Number(radius)}px)
                            </span>
                            <input
                                aria-label="Line corner radius slider"
                                type="range"
                                min="0"
                                max="32"
                                step="1"
                                value={radius}
                                onChange={(event) =>
                                    setRadius(event.target.value)
                                }
                                className="tw-slider mb-1 w-full cursor-pointer"
                            />
                        </label>
                    </>
                )}
                <label className="block">
                    <span className="mb-1 block text-[10px] font-medium text-neutral-600">
                        Y adjustment ({Number(offsetY)}px)
                    </span>
                    <input
                        aria-label="Effect vertical position slider"
                        type="range"
                        min="-40"
                        max="40"
                        step="1"
                        value={offsetY}
                        onChange={(event) => setOffsetY(event.target.value)}
                        className="tw-slider mb-1 w-full cursor-pointer"
                    />
                </label>
                {variant !== 'custom' && (
                    <>
                        <label className="block">
                            <span className="mb-1 block text-[10px] font-medium text-neutral-600">
                                Duration ({Number(durationMs)}ms)
                            </span>
                            <input
                                aria-label="Effect duration slider"
                                type="range"
                                min="0"
                                max="5000"
                                step="50"
                                value={durationMs}
                                disabled={!animate}
                                onChange={(event) =>
                                    setDurationMs(event.target.value)
                                }
                                className="tw-slider mb-1 w-full cursor-pointer disabled:cursor-not-allowed disabled:opacity-40"
                            />
                        </label>
                        <label className="block">
                            <span className="mb-1 block text-[10px] font-medium text-neutral-600">
                                Delay ({Number(delayMs)}ms)
                            </span>
                            <input
                                aria-label="Effect delay slider"
                                type="range"
                                min="0"
                                max="2000"
                                step="50"
                                value={delayMs}
                                disabled={!animate}
                                onChange={(event) =>
                                    setDelayMs(event.target.value)
                                }
                                className="tw-slider mb-1 w-full cursor-pointer disabled:cursor-not-allowed disabled:opacity-40"
                            />
                        </label>
                    </>
                )}
            </div>
            {variant !== 'custom' && (
                <div className="flex items-center justify-between text-[11px] font-medium text-neutral-700">
                    <span>Animate when visible</span>
                    <button
                        type="button"
                        role="switch"
                        aria-label="Animate effect when visible"
                        aria-checked={animate}
                        onClick={() => setAnimate((current) => !current)}
                        className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full transition-colors duration-200 ${
                            animate ? 'bg-primary-600' : 'bg-black/20'
                        }`}
                    >
                        <span
                            aria-hidden="true"
                            className={`inline-block h-4 w-4 translate-y-0.5 rounded-full bg-white shadow-sm transition-transform duration-200 ${
                                animate
                                    ? 'translate-x-[1.15rem]'
                                    : 'translate-x-0.5'
                            }`}
                        />
                    </button>
                </div>
            )}
            {variant === 'highlight' && (
                <div className="flex items-center justify-between text-[11px] font-medium text-neutral-700">
                    <span>Animate from center</span>
                    <button
                        type="button"
                        role="switch"
                        aria-label="Animate highlight from center"
                        aria-checked={direction === 'center'}
                        onClick={() =>
                            setDirection((current) =>
                                current === 'center' ? 'left' : 'center'
                            )
                        }
                        className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full transition-colors duration-200 ${
                            direction === 'center'
                                ? 'bg-primary-600'
                                : 'bg-black/20'
                        }`}
                    >
                        <span
                            aria-hidden="true"
                            className={`inline-block h-4 w-4 translate-y-0.5 rounded-full bg-white shadow-sm transition-transform duration-200 ${
                                direction === 'center'
                                    ? 'translate-x-[1.15rem]'
                                    : 'translate-x-0.5'
                            }`}
                        />
                    </button>
                </div>
            )}
            <div className="flex items-center justify-between text-[11px] font-medium text-neutral-700">
                <span>Shape over text</span>
                <button
                    type="button"
                    role="switch"
                    aria-label="Place shape over text"
                    aria-checked={layer === 'over'}
                    onClick={() =>
                        setLayer((current) =>
                            current === 'over' ? 'under' : 'over'
                        )
                    }
                    className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full transition-colors duration-200 ${
                        layer === 'over' ? 'bg-primary-600' : 'bg-black/20'
                    }`}
                >
                    <span
                        aria-hidden="true"
                        className={`inline-block h-4 w-4 translate-y-0.5 rounded-full bg-white shadow-sm transition-transform duration-200 ${
                            layer === 'over'
                                ? 'translate-x-[1.15rem]'
                                : 'translate-x-0.5'
                        }`}
                    />
                </button>
            </div>
        </fieldset>
    );

    if (!open) return null;

    return (
        <section className="space-y-2 border-t border-neutral-200 pt-3">
            <div className="text-[10px] font-semibold uppercase tracking-wide text-neutral-500">
                Text Effect
            </div>
            <ColorPanel
                isOpen={true}
                colors={colors}
                angle={displayedAngle}
                setAngle={setMarkerAngle}
                addColor={addColor}
                removeColor={removeColor}
                updateColor={updateColor}
                handleHexInputChange={handleHexInputChange}
                handleRemoveColor={removeMarker}
                handleApplyColor={applyMarker}
                removeLabel="Remove"
                angleInputId="marker-gradient-angle"
                gradientAngleOffset={90}
                headerControls={variantControl}
                extraControls={effectControls}
            />
        </section>
    );
}
